/**
 * The advertiser field: type to search the registry (names and aliases) and
 * pick one, or keep a name that isn't there. An ARIA 1.2 combobox — the
 * input owns a listbox popup; ↓/↑ move through it, Enter picks, Escape
 * closes. A typed name that matches nothing stays as typed (the quote then
 * says whether the server knows it), and the last option adds it as a new
 * advertiser.
 */

import React, { useId, useMemo, useState } from 'react';
import type { AdvertiserRecord, MediaPlanCategory } from '../types';
import { historyHint } from '../vocab';
import { fieldStyle, hintStyle, labelStyle } from '../formStyles';
import { FieldError, fieldAria } from './fields';

export const MAX_ADVERTISER_OPTIONS = 8;

interface Props {
  /** The input's id; the field key for errors is 'advertiser'. */
  inputId: string;
  advertisers: AdvertiserRecord[];
  categories: MediaPlanCategory[];
  advertiserKey: string;
  advertiserName: string;
  onPick: (record: AdvertiserRecord) => void;
  /** Free text: the name as typed, with no registry pick. */
  onType: (name: string) => void;
  onAddNew: (name: string) => void;
  /** The "+ Add" lookup is running. */
  adding?: boolean;
  /** The registry record the server matched a typed name to, when there was no pick. */
  matchedName?: string;
  error?: string;
  disabled?: boolean;
}

const norm = (s: string): string => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

/** Registry matches for a query, best first: exact, then name or alias prefix, then anywhere. */
export function matchAdvertisers(advertisers: readonly AdvertiserRecord[], query: string, limit = MAX_ADVERTISER_OPTIONS): AdvertiserRecord[] {
  const q = norm(query);
  if (!q) return [];
  const scored: Array<{ record: AdvertiserRecord; score: number }> = [];
  for (const record of advertisers) {
    const names = [record.name, ...record.aliases].map(norm);
    let score = -1;
    if (names[0] === q) score = 0;
    else if (names.slice(1).includes(q)) score = 1;
    else if (names[0].startsWith(q)) score = 2;
    else if (names.slice(1).some((n) => n.startsWith(q))) score = 3;
    else if (names.some((n) => n.includes(q))) score = 4;
    if (score >= 0) scored.push({ record, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.record.name.localeCompare(b.record.name))
    .slice(0, limit)
    .map((s) => s.record);
}

type Option = { kind: 'record'; record: AdvertiserRecord } | { kind: 'add'; name: string };

const AdvertiserCombobox: React.FC<Props> = ({ inputId, advertisers, categories, advertiserKey, advertiserName, onPick, onType, onAddNew, adding = false, matchedName, error, disabled = false }) => {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const hintId = `${baseId}-hint`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const categoryLabel = useMemo(() => new Map(categories.map((c) => [c.key, c.label])), [categories]);
  const picked = advertiserKey ? advertisers.find((a) => a.key === advertiserKey) : undefined;

  const query = advertiserName;
  const matches = useMemo(() => (picked ? [] : matchAdvertisers(advertisers, query)), [advertisers, query, picked]);
  const exact = matches.some((m) => norm(m.name) === norm(query));
  const options: Option[] = [...matches.map((record): Option => ({ kind: 'record', record })), ...(query.trim() && !exact && !picked ? [{ kind: 'add' as const, name: query.trim() }] : [])];
  const expanded = open && options.length > 0 && !disabled;

  const choose = (option: Option) => {
    setOpen(false);
    setActive(-1);
    if (option.kind === 'record') onPick(option.record);
    else if (!adding) onAddNew(option.name);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!options.length) return;
      setOpen(true);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i < 0 ? (step > 0 ? 0 : options.length - 1) : (i + step + options.length) % options.length));
    } else if (e.key === 'Enter' && expanded && active >= 0 && active < options.length) {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === 'Escape' && expanded) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setActive(-1);
    }
  };

  const hint = picked
    ? [categoryLabel.get(picked.categoryKey) || picked.categoryKey, picked.categoryStatus === 'suggested' ? 'category not confirmed' : null, picked.negotiatedRateCardId ? 'negotiated rates on file' : null, historyHint(picked.pricingSummary)]
        .filter(Boolean)
        .join(' · ')
    : matchedName
      ? `Priced as ${matchedName} (matched by name).`
      : query.trim()
        ? 'Not picked from the list. Pick a match, or add it as a new advertiser.'
        : 'Search by brand name or a name it goes by.';

  return (
    <div>
      <label htmlFor={inputId} style={labelStyle}>
        Advertiser
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          spellCheck={false}
          maxLength={160}
          placeholder="Start typing a brand…"
          style={fieldStyle}
          value={advertiserName}
          disabled={disabled}
          onChange={(e) => {
            onType(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          {...fieldAria('advertiser', error, hintId)}
        />
        <ul id={listId} role="listbox" aria-label="Advertisers" hidden={!expanded} style={styles.list}>
          {options.map((option, i) => (
            <li
              key={option.kind === 'record' ? option.record.key : '__add__'}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              // Keep focus in the input so the pick doesn't blur it first.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(option)}
              onMouseEnter={() => setActive(i)}
              style={{ ...styles.option, ...(i === active ? styles.optionActive : null), ...(option.kind === 'add' ? styles.addOption : null) }}
            >
              {option.kind === 'record' ? (
                <>
                  <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{option.record.name}</span>
                  <span style={styles.optionMeta}>
                    {categoryLabel.get(option.record.categoryKey) || option.record.categoryKey} · {historyHint(option.record.pricingSummary)}
                  </span>
                </>
              ) : adding ? (
                <span>Looking up “{option.name}”…</span>
              ) : (
                <span>+ Add “{option.name}” as a new advertiser</span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <FieldError field="advertiser" message={error} />
      <div id={hintId} style={hintStyle}>
        {hint}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  list: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 'var(--z-flyout)',
    margin: 0,
    padding: 4,
    listStyle: 'none',
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    maxHeight: 300,
    overflowY: 'auto',
  },
  option: { display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 13.5, color: 'var(--fg-2)' },
  optionActive: { background: 'var(--bg-hover)' },
  optionMeta: { fontSize: 11.5, color: 'var(--fg-4)' },
  addOption: { color: 'var(--accent)', fontWeight: 600, borderTop: '1px solid var(--border-subtle)', marginTop: 2 },
};

export default AdvertiserCombobox;
